import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { type QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";

import { type EntityMode } from "../../components/create-data-request-dialog/create-data-request-state";
import { DataRequestEntity } from "../../models/data-request.model";

import { submitDataRequestEntityMutation } from "./data-request.gql";

interface SubmitDataRequestEntityResponse {
  submitDataRequestEntity: DataRequestEntity;
}

const entityQueryKeysByType: Record<EntityMode, (entityId: string) => QueryKey[]> = {
  CLIMATE_ACTION: (entityId) => [queryKeys.climateActions.detail(entityId)],
  INDICATOR: (entityId) => [queryKeys.indicators.detail(entityId), queryKeys.indicators.lists()],
  PROJECT: (entityId) => [queryKeys.projects.detail(entityId)],
};

export interface UseSubmitDataRequestEntityProps {
  onCompleted?: (data: DataRequestEntity) => void;
  onError?: (error: unknown) => void;
}

export const useSubmitDataRequestEntityMutation = ({
  onCompleted,
  onError,
}: UseSubmitDataRequestEntityProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const client = createOperationClient("SubmitDataRequestEntity");
      const data = await client.request<SubmitDataRequestEntityResponse>(submitDataRequestEntityMutation, {
        id,
      });
      return data.submitDataRequestEntity;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataRequests.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.dataRequests.details() });
      queryClient.invalidateQueries({ queryKey: queryKeys.dataRequests.entity(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.details() });
      const entityQueryKeyList = entityQueryKeysByType[data.entityType as EntityMode]?.(data.entityId) ?? [];
      for (const queryKey of entityQueryKeyList) {
        queryClient.invalidateQueries({ queryKey });
      }
      if (onCompleted) onCompleted(data);
    },
    onError: (error) => {
      if (onError) onError(error);
    },
  });

  return {
    submitEntity: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
